import {
    Color,
    Engine,
    Scene,
    SceneActivationContext,
    Sound,
    vec
} from 'excalibur';
import { showMenu } from '../../../../reactUi/showMenu';
import { resources } from '../../../../Resources';
import { makeBlurMaterial } from '../../../../shared/utilities/makeBlurMaterial';
import { StaticHelicopter } from '../../actors/StaticHelicopter';
import { Bobbing } from './Bobbing';
import { InfiniteLayer } from './InfiniteLayer';

export class HelicoptersMenuScene extends Scene {
    private _t = 0;
    private _bobbers: Bobbing[] = [];
    private _rotorInstances: Sound[] = [];

    private clamp01(v: number): number {
        return Math.max(0, Math.min(1, v));
    }

    private easeOutCubic(t: number): number {
        const u = 1 - t;
        return 1 - u * u * u;
    }

    override async onInitialize(engine: Engine): Promise<void> {
        const width = engine.drawWidth;
        const height = engine.drawHeight;

        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteSky, speed: 0, zIndex: -14, screenWidth: width, screenHeight: height }));
        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteDistance, speed: 100, zIndex: -12, screenWidth: width, screenHeight: height, position: vec(0, -100) }));
        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteForeground, speed: 200, zIndex: -11, screenWidth: width, screenHeight: height, position: vec(0, -300) }));
        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteForeground, speed: 450, zIndex: -8, screenWidth: width, screenHeight: height, position: vec(0, -200) }));
        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteBackground, speed: 600, zIndex: -5, screenWidth: width, screenHeight: height }));
        const helicopterDefs: Array<[number, number, number, number, Color]> = [
            [-350, -100, 0.5, 1.2, Color.fromHex('#00725f')],
            [-600, 25, 0.4, 1.6, Color.fromHex('#5a6e00')],
            [150, -50, 0.4, 3, Color.fromHex('#00ac00')],
        ];

        this._bobbers = [];
        this._rotorInstances = [];

        for (const [index, [xOffset, yOffset, rotation, scale, tintColor]] of helicopterDefs.entries()) {
            const x = width / 2 + xOffset;
            const y = (height / 4) * 3 + yOffset;

            const helicopter = new StaticHelicopter(x, y, tintColor);
            helicopter.z = -4;
            helicopter.rotation = rotation;
            helicopter.scale = vec(scale, scale);
            helicopter.name = 'StaticHelicopter_' + (index + 1);
            this.add(helicopter);

            const isLead = index === 2;
            const amp = 50 + Math.random() * 30;
            const period = 3.5 + Math.random() * 2.5;
            const omega = (Math.PI * 2) / period;
            const phase = Math.random() * Math.PI * 2;

            let intro: Bobbing['intro'] | undefined;
            if (isLead) {
                const endX = x;
                const endY = y;
                const startX = -helicopter.width * scale - 50;
                const startY = endY + 300;

                helicopter.pos = vec(startX, startY);

                intro = {
                    active: true,
                    t: 0,
                    dur: 24,
                    startX,
                    startY,
                    endX,
                    endY,
                };
            }

            this._bobbers.push({ actor: helicopter, baseY: y, amp, omega, phase, intro }); 23
        }

        this._bobbers.forEach(() => {
            const rotorSound = new Sound(resources.games.helicopters.actors.helicopter.sfxHuey.path);
            rotorSound.data = resources.games.helicopters.actors.helicopter.sfxHuey.data;
            rotorSound.loop = true;
            this._rotorInstances.push(rotorSound);
        });

        const blur = makeBlurMaterial(engine, 1);
        this.add(new InfiniteLayer({ image: resources.games.helicopters.scenes.menu.spriteForeground, speed: 1200, zIndex: 1, screenWidth: width, screenHeight: height, blur }));
    }

    override onActivate(_context: SceneActivationContext<unknown>): void {
        this._rotorInstances.forEach((sound) => {
            if (!sound.isPlaying()) {
                sound.play({ volume: 0.18 + Math.random() * 0.05 });
            }
        });

        showMenu({ engine: this.engine, options: [['Start Game', '']] }).then(() => this.engine.goToScene('Helicopters'));
    }

    override onPreUpdate(_engine: Engine, elapsedMs: number): void {
        const dt = elapsedMs / 1000;
        this._t += dt;

        for (const b of this._bobbers) {
            // Compute bobbing offset every frame (including during intro)
            const bobOffset = Math.sin(this._t * b.omega + b.phase) * b.amp;

            if (b.intro?.active) {
                b.intro.t += dt;
                const p = this.clamp01(b.intro.t / b.intro.dur);

                // X: ease out
                const ex = this.easeOutCubic(p);
                b.actor.pos.x = b.intro.startX + (b.intro.endX - b.intro.startX) * ex;

                // Y: base path (intro) + arc + bobbing
                const ey = this.easeOutCubic(p);
                const arc = -80 * Math.sin(p * Math.PI); // peaks up then returns to 0
                const baseY = b.intro.startY + (b.intro.endY - b.intro.startY) * ey + arc;

                b.actor.pos.y = baseY + bobOffset;

                if (p >= 1) {
                    // Hand off cleanly: keep the bobbing continuous by anchoring baseY
                    b.intro.active = false;
                    b.baseY = b.intro.endY; // bobbing uses this as the centerline
                    b.actor.pos = vec(b.intro.endX, b.intro.endY + bobOffset);
                }

                continue;
            }

            // Standard bobbing
            b.actor.pos.y = b.baseY + bobOffset;
        }
    }


    override onDeactivate(context: SceneActivationContext): void {
        super.onDeactivate(context);
        for (const rotorSound of this._rotorInstances) rotorSound.stop();
    }
}
